import { Button, FormHelperText, Stack, Typography } from '@mui/material'
import { forwardRef, useCallback, useEffect, useMemo } from 'react'
import {
  FieldError,
  FieldErrors,
  useFieldArray,
  UseFormSetValue,
  UseFormWatch,
} from 'react-hook-form'
import {
  DateTimePickerField,
  Fade,
  FileUploader,
  RadioButtonsGroup,
  Resource,
  TextInputField,
  TextInputFieldProps,
  ToggleInputField,
  ToggleInputFieldProps,
} from 'utoniq-ui'

import {
  CollectionNftInput,
  CollectionStatus,
  CreateCollectionInput,
  ItemNftSalesType,
} from '@/clients/utoniq-core/schema'
import { useConsoleImageEditor } from '@/components/organisms/consoleImageEditor'
import { config } from '@/config'
import {
  handleError,
  tLabel,
  useForm,
  UseFormProps,
  useMessage,
  useTranslation,
} from '@/core'
import { Cell, CellFrame, Organism, OrganismFrame } from '@/core/architecture'
import { convertDatePickerValueToDate, dayjs } from '@/lib/dayjs'

export type CollectionCreateFormProps = {
  artistId: string
  fileUploadInfo: FileUploadInfo
  onSubmit: (values: CreateCollectionProps) => Promise<void>
}

export type CreateCollectionProps = CreateCollectionInput & {
  thumbnailResource: Resource | null
  hasNft: boolean
  isPublishedFlag: boolean
}

export type FileUploadInfo = {
  onDrop: () => Promise<Resource | undefined>
  progress: number
}

const schema: UseFormProps['schema'] = o => ({
  title: o.string().max(100).label(tLabel('consoleCollectionCreate.title')),
  description: o
    .string()
    .max(500)
    .label(tLabel('consoleCollectionCreate.description')),
  startedAt: o
    .date()
    .allow(null, '')
    .optional()
    .label(tLabel('consoleCollectionCreate.startedAt')),
  endedAt: o
    .date()
    .allow(null, '')
    .optional()
    .label(tLabel('consoleCollectionCreate.endedAt')),
  thumbnailPath: o
    .string()
    .label(tLabel('consoleCollectionCreate.thumbnailPath')),
  thumbnailResource: o.object().allow(null).optional(),
  hasNft: o.boolean().allow(false).optional(),
  isPublishedFlag: o.boolean().allow(false).optional(),
  isPublishOnStore: o.boolean().allow(false).optional(),
  nft: o.object({
    salesType: o
      .string()
      .label(tLabel('consoleCollectionCreate.nft.salesType')),
    chains: o.array().items(
      o.object({
        chainName: o.string().allow('').optional(),
        royalty: o
          .number()
          .min(0)
          .max(10)
          .label(tLabel('consoleCollectionCreate.nft.royalty')),
      })
    ),
  }),
})

export const CollectionCreateForm: Organism<CollectionCreateFormProps> = ({
  artistId,
  onSubmit,
  fileUploadInfo,
}) => {
  const { t } = useTranslation('consoleCollectionCreate')
  const { showError } = useMessage()
  const {
    handleSubmit,
    formState: { isSubmitting, errors, isValid },
    register,
    setValue,
    watch,
    control,
    setError,
  } = useForm<CreateCollectionProps>({
    schema,
    defaultValues,
  })
  const watchHasNft = watch('hasNft', false)
  const { fields } = useFieldArray({
    control,
    name: 'nft.chains',
  })

  useEffect(() => {
    if (!watchHasNft) {
      setValue('nft', defaultNft, { shouldValidate: true })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchHasNft])

  const handleOnSubmit = useCallback(
    async (values: CreateCollectionProps) => {
      const inputs = {
        ...values,
        nft: values.hasNft ? values.nft : null,
        status: values.isPublishedFlag
          ? CollectionStatus.Published
          : CollectionStatus.Unpublished,
        startedAt: values.startedAt === '' ? null : values.startedAt,
        endedAt: values.endedAt === '' ? null : values.endedAt,
      }

      await onSubmit(inputs).catch(error => {
        handleError<CreateCollectionProps>(error, setError, showError)
      })
    },
    [onSubmit, setError, showError]
  )

  return (
    <OrganismFrame sx={{ py: 5 }}>
      <TextInputCell
        type="textarea"
        textFieldProps={{
          multiline: true,
          rows: 2,
          InputProps: { sx: { px: 4, py: 3 } },
        }}
        error={errors?.title}
        {...register('title')}
      />
      <TextInputCell
        type="textarea"
        textFieldProps={{
          multiline: true,
          rows: 5,
          InputProps: { sx: { px: 4, py: 3 } },
        }}
        error={errors?.description}
        {...register('description')}
      />
      <CollectionThumbnailInputCell
        artistId={artistId}
        watch={watch}
        setValue={setValue}
        error={errors.thumbnailPath}
        thumbnailUploadInfo={fileUploadInfo}
      />
      <ToggleInputCell
        content={{
          title: t('isPublishedFlag'),
          description: t('isPublishedFlagDesc'),
        }}
        {...register('isPublishedFlag')}
      />
      <CollectionInputDateCell
        errors={errors}
        setValue={setValue}
        startedAt={watch('startedAt')}
        endedAt={watch('endedAt')}
      />
      <ToggleInputCell
        content={{
          title: t('isPublishOnStore'),
          description: t('isPublishOnStoreDesc'),
        }}
        {...register('isPublishOnStore')}
      />
      {config.modules.nft && (
        <>
          <ToggleInputCell
            content={{
              title: t('issuanceOfNFT'),
              description: t('issuanceOfNFTDesc'),
            }}
            {...register('hasNft')}
          />
          {watchHasNft && (
            <Fade>
              <>
                <NftInputCell
                  value={watch('nft.salesType')}
                  setValue={setValue}
                />
                {fields.map((field, index) => (
                  <RoyaltyInputCell
                    key={field.id}
                    inputProps={{
                      type: 'number',
                      error: errors.nft?.chains?.[0]?.royalty,
                      ...register(`nft.chains.${index}.royalty`),
                    }}
                  />
                ))}
              </>
            </Fade>
          )}
        </>
      )}
      <Stack spacing={5} alignItems="center">
        <Button
          size="large"
          sx={{ width: 244 }}
          disabled={!isValid || isSubmitting}
          loading={isSubmitting}
          onClick={handleSubmit(handleOnSubmit)}
        >
          {t('applyForIssuance')}
        </Button>
        {/*
        @note
        一時保存機能実装まで非表示
        https://app.clickup.com/t/908127/UT-6651
        <Button
          variant="outlined"
          size="large"
          sx={{ width: 244 }}
          disabled={!isValid || isSubmitting}
        >
          {t('temporarilySave')}
        </Button> */}
      </Stack>
    </OrganismFrame>
  )
}

export const TextInputCell = forwardRef(
  ({ titleProps, ...props }: Omit<TextInputFieldProps, 't'>, ref) => {
    const { t } = useTranslation('consoleCollectionCreate')
    return (
      <CellFrame>
        <TextInputField
          titleProps={{ ...titleProps, variant: 'h5', color: 'text.primary' }}
          ref={ref}
          {...props}
          t={t}
        />
      </CellFrame>
    )
  }
)

type CollectionThumbnailInputCellProps = {
  artistId: string
  thumbnailUploadInfo: FileUploadInfo
  watch: UseFormWatch<CreateCollectionProps>
  setValue: UseFormSetValue<CreateCollectionProps>
  error?: FieldError
}
export const CollectionThumbnailInputCell: Cell<CollectionThumbnailInputCellProps> =
  ({ artistId, error, watch, setValue, thumbnailUploadInfo }) => {
    const { t } = useTranslation('consoleCollectionCreate')
    const {
      ConsoleImageEditModal,
      ModalProps,
      filePathAfterCrop,
      handleChoseFile,
      handleRemoveFile,
    } = useConsoleImageEditor({
      artistId,
      initialPath: '',
      async callback(data) {
        setValue('thumbnailPath', data.tmpPath ?? '', {
          shouldValidate: true,
        })
      },
    })

    const thumbnailResource = watch('thumbnailResource') as Resource
    const resource = useMemo<Resource | undefined>(() => {
      if (filePathAfterCrop) {
        return {
          id: '',
          resourceName: '',
          imageUploadPath: filePathAfterCrop,
          imageUrl: filePathAfterCrop,
        }
      }
    }, [filePathAfterCrop])

    return (
      <CellFrame sx={{ pb: 8 }}>
        <FileUploader
          sx={{ p: 0 }}
          shape="collection"
          t={t}
          resource={resource ?? thumbnailResource}
          fileUploadInfo={{
            type: 'image',
            async onDrop() {
              await handleChoseFile()
            },
            progress: thumbnailUploadInfo.progress || 0,
            description: t('thumbnailPathDescription'),
            onClose() {
              handleRemoveFile()
              setValue('thumbnailPath', '', {
                shouldValidate: true,
              })
              setValue('thumbnailResource', null, {
                shouldValidate: true,
              })
            },
          }}
          content={{
            title: t('thumbnailPathTitle'),
            contentNode: (
              <Typography variant="body2" color="text.secondary">
                {t('thumbnailPathContentNode')}
              </Typography>
            ),
          }}
        />
        {!!error && <FormHelperText error>{error.message}</FormHelperText>}
        <ConsoleImageEditModal {...ModalProps} />
      </CellFrame>
    )
  }

export const ToggleInputCell = forwardRef(
  (
    {
      sx,
      description,
      ...props
    }: Omit<ToggleInputFieldProps, 't'> & { description?: string },
    ref
  ) => (
    <CellFrame>
      <ToggleInputField
        {...props}
        inputRef={ref}
        sx={{
          px: 0,
          pt: 0,
          '&& .MuiSwitch-switchBase.Mui-checked+.MuiSwitch-track': {
            bgcolor: 'primary.main',
          },
        }}
      />
      <Typography variant="caption" color="text.secondary">
        {description}
      </Typography>
    </CellFrame>
  )
)

type NftInputCellProps = {
  value?: string
  setValue: UseFormSetValue<CreateCollectionProps>
  readOnly?: boolean
}
export const NftInputCell: Cell<NftInputCellProps> = ({
  value,
  setValue,
  readOnly,
}) => {
  const { t } = useTranslation('consoleCollectionCreate')

  const listItems = [
    {
      value: ItemNftSalesType.UserActivation,
      label: t('sellNormallyAndAllowUsers'),
      disabled: readOnly,
    },
    {
      value: ItemNftSalesType.NftSales,
      label: t('soldNFTItem'),
      disabled: readOnly,
    },
  ]

  return (
    <CellFrame sx={{ pb: 8 }}>
      <Typography variant="h5">{t('sellNFTLabel')}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ pt: 3 }}>
        {t('sellNFTDesc')}
      </Typography>
      <RadioButtonsGroup
        row={false}
        value={value}
        title="sellNft"
        listItems={listItems}
        onChange={newValue => {
          if (newValue === listItems[1].value) {
            setValue('nft.salesType', newValue as never, {
              shouldValidate: true,
            })
            return
          }
          setValue('nft.salesType', newValue as never, { shouldValidate: true })
        }}
        t={t}
      />
    </CellFrame>
  )
}

type RoyaltyInputCellProps = {
  inputProps: Omit<TextInputFieldProps, 't'>
}
export const RoyaltyInputCell: Cell<RoyaltyInputCellProps> = ({
  inputProps,
}) => {
  const { t } = useTranslation('consoleCollectionCreate')
  return (
    <CellFrame sx={{ pb: 5 }}>
      <Typography variant="h5">{t('royaltyNFTLabel')}</Typography>
      <Typography variant="body2" color="text.secondary" sx={{ pt: 3 }}>
        {t('royaltyNFTDesc')}
      </Typography>
      <Stack direction="row" alignItems="center" spacing={2} sx={{ mb: 5 }}>
        <TextInputCell
          {...inputProps}
          textFieldProps={{ ...inputProps.textFieldProps, sx: { mb: 0 } }}
        />
        <Typography variant="subtitle1" sx={{ pb: 4 }}>
          %
        </Typography>
      </Stack>
      {!inputProps?.textFieldProps?.disabled && (
        <Stack>
          <Typography
            variant="caption"
            align="center"
            sx={{ color: 'error.main' }}
          >
            {t('issuanceOfNFTNote')}
          </Typography>
        </Stack>
      )}
    </CellFrame>
  )
}

type CollectionInputDateCellProps = {
  setValue: UseFormSetValue<CreateCollectionProps>
  errors?: FieldErrors<CreateCollectionProps>
  startedAt: CreateCollectionProps['startedAt']
  endedAt: CreateCollectionProps['endedAt']
}

export const CollectionInputDateCell: Cell<CollectionInputDateCellProps> = ({
  setValue,
  errors,
  startedAt,
  endedAt,
}) => {
  const { t } = useTranslation('consoleCollectionCreate')

  return (
    <CellFrame sx={{ pb: 8 }}>
      <DateTimePickerField
        onChange={date => {
          setValue(
            'startedAt',
            (convertDatePickerValueToDate(date) || '') as never,
            { shouldValidate: true, shouldDirty: true }
          )
        }}
        value={startedAt ? dayjs(startedAt) : null}
        title={t('startedAt')}
        description={t('startedAtDesc')}
        placeholder={t('startedAtPlaceholder')}
        error={errors?.startedAt}
        titleProps={{ variant: 'subtitle1', color: 'text.primary' }}
      />
      <DateTimePickerField
        onChange={date => {
          setValue(
            'endedAt',
            (convertDatePickerValueToDate(date) || '') as never,
            { shouldValidate: true, shouldDirty: true }
          )
        }}
        value={endedAt ? dayjs(endedAt) : null}
        title={t('endedAt')}
        description={t('endedAtDesc')}
        placeholder={t('endedAtPlaceholder')}
        error={errors?.endedAt}
        titleProps={{ variant: 'subtitle1', color: 'text.primary' }}
      />
    </CellFrame>
  )
}

const defaultNft: CollectionNftInput = {
  salesType: ItemNftSalesType.UserActivation,
  chains: [
    {
      royalty: 10,
      chainName: 'polygon',
    },
  ],
}

const defaultValues: CreateCollectionProps = {
  title: '',
  thumbnailPath: '',
  description: '',
  thumbnailResource: null,
  nft: defaultNft,
  hasNft: false,
  startedAt: null,
  endedAt: null,
  isPublishedFlag: false,
}
